import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'

/**
 * Aplicacao externa da area de trabalho.
 *
 * Espelha `tb_user_app` sem os campos de segredo: a senha nunca vem numa
 * listagem, so pela rota `/apps/:id/credentials`. `hasCredentials` diz se
 * existe uma guardada, que e o suficiente para a UI decidir o que mostrar.
 */
export type UserApp = {
  id: string
  title: string
  url: string
  description: string | null
  icon: string | null
  iconImageId: string | null
  color: string | null
  sortOrder: number
  /** Pasta onde a aplicação está; null = raiz. */
  folderId: string | null
  openMode: 'embedded' | 'tab'
  /** Sigla digitada; vazia = derivada do título. Mostra quando não há logo. */
  abbreviation: string | null
  /** Zoom (%) de abertura no desktop; 100 = normal. */
  zoom: number
  /**
   * - `blocked` — cabeçalho do site proíbe embutir.
   * - `cookies` — carrega, mas a sessão não sobrevive: o cookie é `SameSite`
   *   Lax/Strict e o navegador não o envia dentro de um iframe de outro site.
   *   O usuário loga e cai numa tela vazia.
   * - `fragile` — carrega, mas a página conversa com a janela de cima.
   */
  embedSupport: 'unknown' | 'allowed' | 'blocked' | 'cookies' | 'fragile'
  embedCheckedAt: string | null
  loginUsername: string | null
  loginUpdatedAt: string | null
  createdAt: string
  hasCredentials: boolean
  /** Sou o dono? (compartilhadas comigo vem com isOwner=false) */
  isOwner: boolean
  /** Foi compartilhada comigo por outra pessoa? */
  shared: boolean
  /** Compartilhada com todos do workspace (info do dono). */
  sharedEveryone: boolean
}

export type AppInput = {
  title: string
  url: string
  description?: string | null
  icon?: string | null
  iconImageId?: string | null
  color?: string | null
  openMode?: 'embedded' | 'tab'
  /** Sigla digitada (até 6 letras); null/vazia = derivada do título. */
  abbreviation?: string | null
  /** Zoom (%) de abertura no desktop; 100 = normal. */
  zoom?: number
  loginUsername?: string | null
  /** Ausente mantem a senha guardada; `null` ou vazio apaga. */
  loginPassword?: string | null
}

const CHAVE = ['apps'] as const

async function corpo<T>(resposta: Response): Promise<T> {
  const dados = (await resposta.json()) as T & { error?: { message?: string } }
  if (!resposta.ok) throw new Error(dados.error?.message ?? 'Não foi possível concluir a operação.')
  return dados
}

export function useApps() {
  return useQuery({
    queryKey: CHAVE,
    queryFn: async () => {
      const resposta = await api.apps.$get()
      const dados = await corpo<{ apps: UserApp[] }>(resposta)
      return dados.apps
    },
  })
}

export function useCreateApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entrada: AppInput) => {
      const resposta = await api.apps.$post({ json: entrada as never })
      const dados = await corpo<{ app: UserApp }>(resposta)
      return dados.app
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useUpdateApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...entrada }: AppInput & { id: string }) => {
      const resposta = await api.apps[':id'].$patch({ param: { id }, json: entrada as never })
      const dados = await corpo<{ app: UserApp }>(resposta)
      return dados.app
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useDeleteApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const resposta = await api.apps[':id'].$delete({ param: { id } })
      await corpo<{ ok: true }>(resposta)
      return id
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

/** Reordena as aplicações (arrastar na lista). Update otimista para não piscar. */
export function useReorderApps() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const resposta = await api.apps.reorder.$post({ json: { ids } })
      await corpo<{ ok: true }>(resposta)
      return ids
    },
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: CHAVE })
      const anterior = qc.getQueryData<UserApp[]>(CHAVE)
      if (anterior) {
        const porId = new Map(anterior.map((a) => [a.id, a]))
        const nova = ids.map((id) => porId.get(id)).filter((a): a is UserApp => Boolean(a))
        for (const a of anterior) if (!ids.includes(a.id)) nova.push(a)
        qc.setQueryData<UserApp[]>(CHAVE, nova)
      }
      return { anterior }
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.anterior) qc.setQueryData(CHAVE, ctx.anterior)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

/** Re-sonda o site. Devolve o suporte a iframe recalculado. */
export function useProbeApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const resposta = await api.apps[':id'].probe.$post({ param: { id } })
      const dados = await corpo<{ embedSupport: UserApp['embedSupport'] }>(resposta)
      return dados.embedSupport
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

/**
 * Busca a senha em texto.
 *
 * Nao e `useQuery` de proposito: cachear senha em memoria do cliente de query,
 * onde ela ficaria viva ate o GC do TanStack, seria pior do que buscar de novo
 * nas poucas vezes em que ela e necessaria.
 */
export async function buscarCredenciais(id: string) {
  const resposta = await api.apps[':id'].credentials.$get({ param: { id } })
  const dados = await corpo<{ credentials: { username: string | null; password: string | null } }>(resposta)
  return dados.credentials
}

/** Busca o favicon do site e o guarda como imagem; devolve o iconImageId. */
export async function buscarFaviconDoSite(url: string): Promise<string> {
  const resposta = await api.apps.favicon.$post({ json: { url } })
  const dados = await corpo<{ iconImageId: string; url: string }>(resposta)
  return dados.iconImageId
}

/** Compartilhamento atual (só o dono). */
export async function buscarShare(id: string) {
  const resposta = await api.apps[':id'].share.$get({ param: { id } })
  return corpo<{ everyone: boolean; memberIds: string[] }>(resposta)
}

/** Define com quem a aplicação é compartilhada (todos ou membros). */
export async function salvarShare(id: string, dados: { everyone: boolean; memberIds: string[] }) {
  const resposta = await api.apps[':id'].share.$put({ param: { id }, json: dados })
  return corpo<{ everyone: boolean; memberIds: string[] }>(resposta)
}

/** Grava a credencial (do dono ou a do próprio recipiente numa compartilhada). */
export async function salvarCredencial(
  id: string,
  dados: { loginUsername?: string | null; loginPassword?: string | null },
) {
  const resposta = await api.apps[':id'].credentials.$put({ param: { id }, json: dados })
  return corpo<{ ok: boolean }>(resposta)
}

// -------------------------------------------------------------- pastas

export type AppFolder = {
  id: string
  name: string
  parentFolderId: string | null
  sortOrder: number
  /** Sou o dono? Pastas compartilhadas (do dono de um app compartilhado) vêm com owner=false e são só leitura. */
  owner: boolean
}

const PASTAS = ['app-folders'] as const

export function useFolders() {
  return useQuery({
    queryKey: PASTAS,
    queryFn: async () => {
      const resposta = await api.apps.folders.$get()
      const dados = await corpo<{ folders: AppFolder[] }>(resposta)
      return dados.folders
    },
  })
}

export function useCreateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { name: string; parentFolderId?: string | null }) => {
      const resposta = await api.apps.folders.$post({ json: dados })
      return (await corpo<{ folder: AppFolder }>(resposta)).folder
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PASTAS }),
  })
}

export function useUpdateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...dados }: { id: string; name?: string; parentFolderId?: string | null }) => {
      const resposta = await api.apps.folders[':id'].$patch({ param: { id }, json: dados })
      return (await corpo<{ folder: AppFolder }>(resposta)).folder
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PASTAS }),
  })
}

export function useDeleteFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const resposta = await api.apps.folders[':id'].$delete({ param: { id } })
      await corpo<{ ok: true }>(resposta)
      return id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PASTAS })
      qc.invalidateQueries({ queryKey: CHAVE })
    },
  })
}

/** Reordena pastas (mesma ordem = sortOrder por posição). */
export function useReorderFolders() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const resposta = await api.apps.folders.reorder.$post({ json: { ids } })
      await corpo<{ ok: true }>(resposta)
      return ids
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-folders'] }),
  })
}

/** Move uma aplicação para uma pasta (ou para a raiz com null). Otimista. */
export function useMoveApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, folderId }: { id: string; folderId: string | null }) => {
      const resposta = await api.apps[':id'].$patch({ param: { id }, json: { folderId } as never })
      await corpo<{ app: UserApp }>(resposta)
      return { id, folderId }
    },
    onMutate: async ({ id, folderId }) => {
      await qc.cancelQueries({ queryKey: CHAVE })
      const anterior = qc.getQueryData<UserApp[]>(CHAVE)
      if (anterior) {
        qc.setQueryData<UserApp[]>(
          CHAVE,
          anterior.map((a) => (a.id === id ? { ...a, folderId } : a)),
        )
      }
      return { anterior }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.anterior) qc.setQueryData(CHAVE, ctx.anterior)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}
